import type { TechlineAssetEntity } from '../entities';

import { StsBackendOperation } from '../../../sts-backend-operation';
import { toTechlineAssetEntity } from '../helpers';

/** The result of a single asset completion check. */
export type AssetCompletionCheckResult = {
    /** Assets that have finished processing (completed or errored). */
    settled: TechlineAssetEntity[];

    /** Assets that are still uploading. */
    stillUploading: TechlineAssetEntity[];
};

/** Parameters for checking asset completion status. */
export type CheckForCompletionParams = {
    /** IDs of assets currently in Uploading state. */
    uploadingAssetIds: string[];
};

/**
 * Check the current processing status of uploading assets. Fetches
 * each asset's latest state and categorizes them as settled (no longer
 * uploading) or still uploading. This is a pure single-iteration check
 * with no awareness of polling, retries, or thresholds.
 */
export class CheckForCompletion extends StsBackendOperation {
    /**
     * Fetch the latest status of each uploading asset and categorize them.
     *
     * @param params - IDs of assets currently in Uploading state
     * @returns settled and still-uploading assets as domain entities
     */
    async exec(params: CheckForCompletionParams): Promise<AssetCompletionCheckResult> {
        const { uploadingAssetIds } = params;

        if (!uploadingAssetIds.length) {
            return { settled: [], stillUploading: [] };
        }

        // Fetch the latest state for each uploading asset in parallel
        const responses = await Promise.all(uploadingAssetIds.map((assetId) => this.client.retrieveAsset({ assetId })));

        // Categorize each asset based on its current processing state
        const settled: TechlineAssetEntity[] = [];
        const stillUploading: TechlineAssetEntity[] = [];

        for (const response of responses) {
            if (response.success) {
                const asset = response.data?.results?.asset;

                if (!asset) {
                    continue;
                }

                const entity = toTechlineAssetEntity(asset);

                if (entity.status === 'uploading') {
                    stillUploading.push(entity);
                } else {
                    settled.push(entity);
                }
            }
        }

        return { settled, stillUploading };
    }
}
